import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

export const SuccessStep: React.FC<OnboardingStepProps> = ({
  data,
  onFinish,
}) => {
  const { t } = useLanguage();

  const handleFinish = () => {
    if (onFinish) {
      onFinish();
    }
  };

  return (
    <OnboardingStep
      title={t('onboarding.success.title')}
      description={t('onboarding.success.description')}
      onNext={() => {}}
      onPrevious={() => {}}
      onFinish={handleFinish}
      canGoNext={false}
      showNext={false}
      showPrevious={false}
      showFinish={true}
    >
      <div className="text-center space-y-8">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('onboarding.success.congratulations')}
          </h2>
          <p className="text-lg text-gray-600">
            {data.clientType === 'SUPER_TENANT_ADMIN' 
              ? t('onboarding.success.superTenantReady')
              : t('onboarding.success.tenantReady')
            }
          </p>
        </div>

        {/* What's Next */}
        <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.success.whatsNext')}
          </h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium text-sm">1</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {t('onboarding.success.step1.title')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('onboarding.success.step1.description')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium text-sm">2</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {t('onboarding.success.step2.title')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('onboarding.success.step2.description')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium text-sm">3</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {t('onboarding.success.step3.title')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('onboarding.success.step3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              {t('onboarding.success.actions.users')}
            </h4>
            <p className="text-xs text-gray-600">
              {t('onboarding.success.actions.usersDescription')}
            </p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              {t('onboarding.success.actions.roles')}
            </h4>
            <p className="text-xs text-gray-600">
              {t('onboarding.success.actions.rolesDescription')}
            </p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              {t('onboarding.success.actions.hierarchies')}
            </h4>
            <p className="text-xs text-gray-600">
              {t('onboarding.success.actions.hierarchiesDescription')}
            </p>
          </div>
        </div>

        {/* Support Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .994-.24 1.929-.655 2.716l-.717-.717A6.5 6.5 0 0016.5 10c0-3.59-2.91-6.5-6.5-6.5S3.5 6.41 3.5 10c0 .994.24 1.929.655 2.716l-.717.717A8 8 0 1116 10z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('onboarding.success.needHelp')}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>{t('onboarding.success.supportDescription')}</p>
                <p className="mt-1">
                  <a href="/support" className="font-medium underline hover:text-blue-600">
                    {t('onboarding.success.contactSupport')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingStep>
  );
};
